/**
 * StuckWriteNotice — 写请求久等未回时的全局出口（#356 目标 4）。
 *
 * 传输层保证 30s 内必 settle（PR #434），页面不会永远挂住；但这 30 秒里运营看到的只是一个
 * disabled 的按钮：不知道是自己网慢、服务端卡住，还是点击根本没生效，也没法中断去做别的事。
 * 本组件订阅 `api/inflight-writes` 的登记表，任一**写**请求等待超过 `STUCK_THRESHOLD_MS`
 * 就浮出一条提示 + 「取消等待」。
 *
 * 取消不需要任何页面配合：`api/client.ts` 把中止分流成 `AdminRequestCanceledError`，
 * 各页既有 `onError` 兜底照常拿到它并展示 `REQUEST_CANCELED` 的中文文案。
 *
 * **文案不说「已取消操作」，只说「停止等待」**：中止发生在浏览器本地，服务端可能已经收到并落库
 * （`AdminRequestAbortedError.serverStateUnknown`，非 GET 恒 true）。说成「已取消」是替服务端做了
 * 一个前端无从知道的判断，运营据此重试会造成重复写入 —— 新建类端点尤甚。
 *
 * 挂在 `App` 根部而非某个页面：它要覆盖全部 105 个 mutation 站点，逐页挂会漏且无法维持一致。
 */

import { useEffect, useState, useSyncExternalStore } from 'react';
import {
  type InflightWrite,
  listInflightWrites,
  subscribeInflightWrites,
} from '~/api/inflight-writes';
import { Icons } from '~/lib/icons';

/**
 * 超过多久算「久等」。取 8s：短于它的写请求本就该等（正常保存 1-2s，批量翻译略久），
 * 过早浮出提示会训练运营忽略它；而传输层硬超时是 30s，8s 给出的 22 秒窗口足够运营决定去留。
 */
export const STUCK_THRESHOLD_MS = 8_000;

/** 已等待时长的刷新频率。只在有在飞写请求时起定时器，空闲期不留常驻 timer。 */
const TICK_MS = 1_000;

function elapsedSeconds(w: InflightWrite, now: number): number {
  return Math.floor((now - w.startedAt) / 1000);
}

export function StuckWriteNotice() {
  const writes = useSyncExternalStore(subscribeInflightWrites, listInflightWrites);
  const [now, setNow] = useState(() => performance.now());

  useEffect(() => {
    // 先立刻重算一次：登记表刚变化时若等下一个 tick，新请求要晚一秒才参与判定，
    // 而「请求已结束、提示还挂着」也要多显示一秒
    setNow(performance.now());
    // 空闲期不留常驻 timer
    if (writes.length === 0) return;
    const t = setInterval(() => setNow(performance.now()), TICK_MS);
    return () => clearInterval(t);
  }, [writes]);

  const stuck = writes.filter((w) => now - w.startedAt >= STUCK_THRESHOLD_MS);
  if (stuck.length === 0) return null;

  // 多条同时卡住时只呈现最久的那条 + 计数：逐条列出会在窄屏堆成一片，而运营要做的决定是一样的
  const oldest = stuck.reduce((a, b) => (a.startedAt <= b.startedAt ? a : b));

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        bottom: 20,
        left: 20,
        zIndex: 9998, // 低于 ToastHost(9999)：取消后弹出的 toast 应压在它上面
        maxWidth: 420,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '10px 14px',
        borderRadius: 'var(--radius-md)',
        background: 'var(--warn)',
        color: 'white',
        boxShadow: 'var(--shadow-lg)',
        fontSize: 12.5,
        overflowWrap: 'anywhere',
      }}
    >
      <Icons.IconAlert size={14} style={{ flexShrink: 0, marginTop: 2 }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div>
          请求已等待 <span className="mono">{elapsedSeconds(oldest, now)}s</span> 仍未返回
          {stuck.length > 1 ? `（共 ${stuck.length} 个）` : ''}。
        </div>
        <div style={{ opacity: 0.9 }}>
          <span className="mono">
            {oldest.method} {oldest.path}
          </span>
        </div>
        <div style={{ opacity: 0.9 }}>
          可以继续等（最多 30 秒后自动结束），或停止等待去做别的事。
          <strong>停止等待只中断本地等候，服务端可能已经处理完</strong>，之后请刷新确认结果。
        </div>
        <div>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => {
              for (const w of stuck) w.abort();
            }}
          >
            停止等待{stuck.length > 1 ? `（${stuck.length} 个）` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
